import { Role, rolesManipulator } from 'auth';
import { RouteObject } from 'react-router-dom';
import NotificationsPage from '@/modules/common/pages/NotificationsPage.tsx';
import Reports from '@/modules/common/pages/Reports.tsx';
import IssuerDashboardWrapper from '@/modules/issuer-dashboard/components/IssuerDashboardWrapper.tsx';
import IssuerAIChat from '@/modules/issuer-dashboard/pages/IssuerAIChat.tsx';
import IssuerBuyBackNew from '@/modules/issuer-dashboard/pages/IssuerBuyBackNew.tsx';
import IssuerCertificates from '@/modules/issuer-dashboard/pages/IssuerCertificates.tsx';
import IssuerClass from '@/modules/issuer-dashboard/pages/IssuerClass.tsx';
import IssuerClasses from '@/modules/issuer-dashboard/pages/IssuerClasses.tsx';
import IssuerRegisters from '@/modules/issuer-dashboard/pages/IssuerClasses.tsx';
import IssuerCommunications from '@/modules/issuer-dashboard/pages/IssuerCommunication.tsx';
import IssuerDashboard from '@/modules/issuer-dashboard/pages/IssuerDashboard.tsx';
import IssuerDistributionDashboard from '@/modules/issuer-dashboard/pages/IssuerDistributionDashboard.tsx';
import IssuerDistributionsEvents from '@/modules/issuer-dashboard/pages/IssuerDistributionsEvents.tsx';
import IssuerDistributionsPayments from '@/modules/issuer-dashboard/pages/IssuerDistributionsPayments.tsx';
import IssuerDistributionsReinvestments from '@/modules/issuer-dashboard/pages/IssuerDistributionsReinvestments.tsx';
import IssuerDividends from '@/modules/issuer-dashboard/pages/IssuerDividends.tsx';
import IssuerDocuments from '@/modules/issuer-dashboard/pages/IssuerDocuments.tsx';
import IssuerHolder from '@/modules/issuer-dashboard/pages/IssuerHolder.tsx';
import IssuerHolderUpdate from '@/modules/issuer-dashboard/pages/IssuerHolderUpdate.tsx';
import IssuerHolders from '@/modules/issuer-dashboard/pages/IssuerHolders.tsx';
import IssuerIssuers from '@/modules/issuer-dashboard/pages/IssuerIssuers.tsx';
import IssuerNavManagement from '@/modules/issuer-dashboard/pages/IssuerNavManagement.tsx';
import IssuerOperations from '@/modules/issuer-dashboard/pages/IssuerOperations.tsx';
import IssuerPortfolio from '@/modules/issuer-dashboard/pages/IssuerPortfolio.tsx';
import IssuerPortfolios from '@/modules/issuer-dashboard/pages/IssuerPortfolios.tsx';
import IssuerSecuritiesDashboard from '@/modules/issuer-dashboard/pages/IssuerSecuritiesDashboard.tsx';
import IssuerTransactions from '@/modules/issuer-dashboard/pages/IssuerTransactions.tsx';
import IssuerTransfers from '@/modules/issuer-dashboard/pages/IssuerTransfers.tsx';

export const issuerDashboardPaths = {
  static: {
    dashboard: '/issuer/dashboard',

    securities: '/issuer/securities',
    classes: '/issuer/classes',
    nav: '/issuer/nav',
    transactions: '/issuer/transactions',
    transfers: '/issuer/transfers',
    buyBack: '/issuer/buy-backs',
    certificates: '/issuer/certificates',

    distributions: '/issuer/distributions',
    dividends: '/issuer/dividends',
    events: '/issuer/events',
    reInvestments: '/issuer/re-investments',
    payments: '/issuer/payments',

    aiChat: '/issuer/ai-chat',

    createHolder: '/issuer/create-holder',
    updateHolder: '/issuer/update-holder',
    holders: '/issuer/holders',
    reports: '/issuer/reports',
    documents: '/issuer/documents',
    communications: '/issuer/communications',
    notifications: '/issuer/notifications',

    operations: '/issuer/operations',
    issuers: '/issuer/issuers',
    registers: '/issuer/register-classes',

    blockchain: '/issuer/blockchain',
    meetings: '/issuer/meetings',

    portfolios: '/issuer/portfolios',
  },
  dynamic: {
    updateHolder: (id = ':id') => {
      return `${issuerDashboardPaths.static.updateHolder}/${id}`;
    },
    holder: (id = ':id') => {
      return `${issuerDashboardPaths.static.holders}/${id}`;
    },
    classById: (id = ':id') => {
      return `${issuerDashboardPaths.static.classes}/${id}`;
    },
    portfolio: (id = ':id') => `${issuerDashboardPaths.static.portfolios}/${id}`,
  },
};

export const getIssuerDashboardRoutes = () => {
  const userRole = rolesManipulator.current_user_role;

  const router: RouteObject[] = [
    {
      element: <IssuerDashboardWrapper />,
      children: [
        { path: issuerDashboardPaths.static.dashboard, element: <IssuerDashboard /> },
        { path: issuerDashboardPaths.static.operations, element: <IssuerOperations /> },
        { path: issuerDashboardPaths.static.securities, element: <IssuerSecuritiesDashboard /> },
        { path: issuerDashboardPaths.static.classes, element: <IssuerClasses /> },
        { path: issuerDashboardPaths.dynamic.classById(), element: <IssuerClass /> },
        { path: issuerDashboardPaths.static.nav, element: <IssuerNavManagement /> },
        { path: issuerDashboardPaths.static.transactions, element: <IssuerTransactions /> },
        { path: issuerDashboardPaths.static.transfers, element: <IssuerTransfers /> },
        { path: issuerDashboardPaths.static.buyBack, element: <IssuerBuyBackNew /> },
        { path: issuerDashboardPaths.static.certificates, element: <IssuerCertificates /> },
        { path: issuerDashboardPaths.static.createHolder, element: <IssuerHolderUpdate /> },
        {
          path: issuerDashboardPaths.dynamic.updateHolder(),
          element: <IssuerHolderUpdate />,
        },
        {
          path: issuerDashboardPaths.static.distributions,
          element: <IssuerDistributionDashboard />,
        },
        { path: issuerDashboardPaths.static.events, element: <IssuerDistributionsEvents /> },
        {
          path: issuerDashboardPaths.static.reInvestments,
          element: <IssuerDistributionsReinvestments />,
        },
        { path: issuerDashboardPaths.static.payments, element: <IssuerDistributionsPayments /> },
        { path: issuerDashboardPaths.static.aiChat, element: <IssuerAIChat /> },
        { path: issuerDashboardPaths.static.holders, element: <IssuerHolders /> },
        ...(userRole === Role.SR_ISSUER_ADMIN
          ? [{ path: issuerDashboardPaths.static.issuers, element: <IssuerIssuers /> }]
          : []),
        { path: issuerDashboardPaths.static.registers, element: <IssuerRegisters /> },
        { path: issuerDashboardPaths.static.dividends, element: <IssuerDividends /> },
        { path: issuerDashboardPaths.static.reports, element: <Reports /> },
        { path: issuerDashboardPaths.static.documents, element: <IssuerDocuments /> },
        { path: issuerDashboardPaths.static.communications, element: <IssuerCommunications /> },
        { path: issuerDashboardPaths.static.notifications, element: <NotificationsPage /> },
        { path: issuerDashboardPaths.dynamic.holder(), element: <IssuerHolder /> },
        { path: issuerDashboardPaths.static.portfolios, element: <IssuerPortfolios /> },
        { path: issuerDashboardPaths.dynamic.portfolio(), element: <IssuerPortfolio /> },
      ],
    },
  ];

  return router;
};
